import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import VisualBlock from '@features/cms/components/collections/VisualBlock/VisualBlock';
import VisualStickyBar from '@features/cms/components/collections/VisualBlock/VisualStickyBar/VisualStickyBar';
import { ICmsDocument, IContentNavigation, IVisualBlock, TCmsBlocks } from '@features/cms/typescript';
import { ICategory, IListingData, TCategoriesTree, IArrangementProduct, INavigationVirtualCategory, IVirtualCategoryFilters } from '@features/categories/typescript';
import ProductsListing from '@features/simple/sections/ProductsListing/ProductsListing';
import { IInfoCardWithMedia } from '@components/typescript';
import { URL_QUERY_MAP } from '@features/categories/const';
import { useFiltersStore } from '@features/categories/mobx/stores/FiltersStoreProvider';
import { getSelectedFilters } from '@utils/filters';
import ItemListSchema from '@components/ItemListSchema/ItemListSchema';
import NavigationVirtualCategories from '@features/categories/components/NavigationVirtualCategories/NavigationVirtualCategories';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { logScrollTrigger } from '@utils/scrollDebug';
type PropsType = {
  children: React.ReactNode;
  categoryPageContent: Record<string, TCmsBlocks> | null;
  subpages: ICmsDocument[];
  categoryId: number;
  categorySlug: string;
  categoryName: string;
  categoryChildren: Record<symbol, ICategory>;
  activeSlug: string;
  categoriesTree: TCategoriesTree;
  listingData: IListingData;
  arrangementProducts: IArrangementProduct[];
  infoCards: IInfoCardWithMedia[];
  contentNavigation?: IContentNavigation[] | null;
  categoryBlocks?: Record<string, TCmsBlocks> | null;
  virtualCategorySlug: string | null;
  virtualCategoryFilters: IVirtualCategoryFilters;
  navigationVirtualCategories: INavigationVirtualCategory[];
};
const CollectionLayout = ({
  children,
  categoryId,
  categorySlug,
  categoryName,
  categoryChildren,
  activeSlug,
  subpages,
  categoryPageContent,
  categoriesTree,
  listingData,
  arrangementProducts,
  infoCards,
  contentNavigation = null,
  categoryBlocks = null,
  virtualCategorySlug,
  virtualCategoryFilters,
  navigationVirtualCategories
}: PropsType) => {
  const [newActiveSlug, setNewActiveSlug] = useState<string>(activeSlug);
  const router = useRouter();
  const visualBlockRef = useRef<HTMLDivElement>(null);
  const {
    t
  } = useTranslation('common');
  const filtersStore = useFiltersStore(listingData, arrangementProducts, t('sort'), categoryId, router.asPath, virtualCategoryFilters, virtualCategorySlug);
  const [hasProducts, setHasProducts] = useState<boolean>(false);
  useEffect(() => {
    if (categoryId !== filtersStore.categoryId) {
      filtersStore.populateStoreWithData(listingData, t('sort'), categoryId, router.asPath);
      filtersStore.setArrangementProducts(arrangementProducts);
    }
  }, [categoryId]);
  useEffect(() => {
    if (virtualCategorySlug && virtualCategorySlug !== filtersStore.virtualCategorySlug) {
      filtersStore.clearVirtualCategoryFilters();
      filtersStore.virtualCategoryFilters = virtualCategoryFilters;
      filtersStore.populateStoreWithData(listingData, t('sort'), categoryId);
    }
  }, [virtualCategorySlug]);
  let headerBlock: TCmsBlocks | null = null;
  if (categoryPageContent) {
    const keys = Object.keys(categoryPageContent);
    keys.forEach(key => {
      if (categoryPageContent[key].type === 'visualBlock') {
        headerBlock = categoryPageContent[key];
      }
    });
  }
  const handleScroll = (trigger: string) => {
    const visualBlockHeight = visualBlockRef.current?.clientHeight || 0;
    const top = visualBlockHeight + 10;
    logScrollTrigger('CollectionLayout:scrollUnderVisualBlock — window.scrollTo', {
      trigger,
      top,
      visualBlockHeight,
      initAddress: filtersStore.initAddress
    });
    window.scrollTo({
      top
    });
  };
  useEffect(() => {
    if (virtualCategorySlug) {
      setNewActiveSlug('products');
    } else if (activeSlug !== newActiveSlug) {
      setNewActiveSlug(activeSlug);
    }
  }, [activeSlug, virtualCategorySlug, newActiveSlug]);
  useEffect(() => {
    if (filtersStore?.products) {
      const {
        products,
        filterKeys: filtersIterator,
        filters
      } = filtersStore;
      const selectedFilters = getSelectedFilters(filtersIterator, filters);
      setHasProducts(products.length > 0 || selectedFilters.length > 0);
    } else {
      setHasProducts(false);
    }
    // MobX observer automatycznie obsługuje re-rendery gdy products się zmienia
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersStore?.products?.length]);
  const pageValue = router.query.page;
  const currentPage = pageValue ? parseInt(pageValue as string, 10) : 1;
  const products = listingData?.products || [];
  useEffect(() => {
    const handleRouteChangeComplete = (newPath: string, routeProps?: {
      shallow?: boolean;
    }) => {
      // Don't scroll if there's a hash in URL - let hash handler take care of it
      const hasHash = typeof window !== 'undefined' && window.location.hash && window.location.hash.length > 1;
      const isShallow = routeProps?.shallow === true;
      // Compare path only — query changes (filters) must not trigger scroll-to-listing
      const nextPathname = newPath.split('?')[0];
      const initPathname = (filtersStore.initAddress || '').split('?')[0];
      const isRestoringListingProduct = layoutUiState.listingScrollRestorePending || layoutUiState.lastVisitedProductId !== null;
      const shouldScrollUnderVisualBlock = !isRestoringListingProduct && !hasHash && !!visualBlockRef.current && !isShallow && nextPathname !== initPathname;
      logScrollTrigger('CollectionLayout:routeChangeComplete — evaluate scroll', {
        newPath,
        initAddress: filtersStore.initAddress,
        hasHash,
        shallow: isShallow,
        isRestoringListingProduct,
        shouldScrollUnderVisualBlock
      });
      if (shouldScrollUnderVisualBlock) {
        setTimeout(() => {
          if (layoutUiState.listingScrollRestorePending || layoutUiState.lastVisitedProductId !== null) return;
          handleScroll('routeChangeComplete');
        }, 300);
      }
      if (newPath.includes('type') && !isShallow) {
        filtersStore.filters.forEach(f => {
          if (f.queryName.toLowerCase() === URL_QUERY_MAP.PRODUCT_TYPE) {
            f.setValueFromURLString();
          }
        });
      }
      filtersStore.updateAddress(newPath);
    };
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);
  if (!categoryPageContent) {
    return null;
  }
  return <>
      {products.length > 0 && <ItemListSchema products={products} currentPage={currentPage} totalItems={listingData?.pagination?.count} />}
      <VisualStickyBar categorySlug={categorySlug} activeSlug={newActiveSlug} subpages={subpages} visualBlockRef={visualBlockRef} setNewActiveSlug={setNewActiveSlug} hasProducts={hasProducts} contentNavigation={contentNavigation} />
      {headerBlock && <VisualBlock ref={visualBlockRef} data={headerBlock ?? {} as IVisualBlock} subpages={subpages} categorySlug={categorySlug} activeSlug={newActiveSlug} setNewActiveSlug={setNewActiveSlug} hasProducts={hasProducts} isFirst />}
      <div>
        {children}
        <ProductsListing categoryId={categoryId} categoryName={categoryName} categoryChildren={categoryChildren} filtersStore={filtersStore} infoCards={infoCards} isCategoryPage categoriesTree={categoriesTree} hasProducts={hasProducts} blocks={categoryBlocks} />
      </div>
      {navigationVirtualCategories && navigationVirtualCategories.length > 0 && <NavigationVirtualCategories navigationVirtualCategories={navigationVirtualCategories} />}
    </>;
};
export default observer(CollectionLayout);